import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import WhatsAppFloat from '../components/WhatsAppFloat';
import SEO from '../components/SEO';
import { PersonSchema, BreadcrumbSchema } from '../utils/schemas';

function SobreMiPage() {
  const exhibitions = [
    { year: '2025', title: 'Fotonoche — Proyección Internacional de Fotografía, Frenesí', location: 'Centro de Arte Alcobendas, Madrid, España' },
    { year: '2025', title: 'Fotonoche Cordada — Festival Internacional de Fotografía, Frenesí', location: 'Centro Cultural UNC, Córdoba, Argentina' },
    { year: '2024', title: '"EnChroma" — Exposición internacional', location: 'Embajada de México en Uruguay' },
    { year: '2024', title: '"Ecos Cardinales" — Exposición internacional', location: 'Centro Cultural Casa Talaveras, México' },
    { year: '2023', title: '"Pateando Calle" — Fotoseptiembre PTY23', location: '' },
    { year: '2023', title: '"Expo 47 CFP" — Club Fotográfico De Panamá', location: 'Galería E. Amador, Universidad de Panamá' },
    { year: '2022', title: '"Pateando Calle" — Exposición individual', location: 'Alianza Francesa de Panamá · Curada por Ileana Forero' },
    { year: '2022', title: '"Expo 46 CFP" — Club Fotográfico De Panamá', location: 'Festival de Fotografía de Antioquia, Jericó, Medellín · Galería Juan Manuel Cedeño, San Felipe · Galería E. Amador, Universidad de Panamá' },
    { year: '2021', title: '"Pateando Calle" — Galería digital Photolab', location: '' },
    { year: '2021', title: '"La Fotografía Como Arte Social" — Memoria Cultural', location: '' },
    { year: '2021', title: '"Expo 45 CFP" — Club Fotográfico De Panamá', location: '' },
    { year: '2020', title: '"La Nueva Normalidad" — Memoria Cultural', location: '' },
    { year: '2019', title: '"Identidad, memoria y cultura" — Panamá Instantánea', location: '' },
  ];

  const awards = [
    { year: '2023', title: 'World Photographic Cup — Team Panama, Singapore' },
    { year: '2023', title: 'PSA Gold Medal — "A La Espera Del Conquistador", Intercontinental Circuit, Salón Panamá' },
    { year: '2023', title: 'Diploma Intercontinental Circuit — Salón Andorra, "A La Espera Del Conquistador"' },
    { year: '2023', title: '1st Place — Concurso Bienvenido a Panamá, Pexels Latam' },
    { year: '2023', title: '2nd Place — Concurso Pasaporte Panamá, Pexels Latam' },
    { year: '2022', title: 'Top 35 Panama — 7th International 35 Awards, EPHOTO Moscow' },
    { year: '2022', title: '3rd Place "Rostros de la Diversidad" — 11º MARPAT, Club Fotográfico De Panamá' },
    { year: '2021', title: 'Winner — Nuevos Talentos Latinoamericanos, Festival Internacional Santa Marta, Colombia' },
    { year: '2021', title: '2nd Place "La Nueva Normalidad" — 10º MARPAT, Club Fotográfico De Panamá' },
    { year: '2021', title: 'Top 35 Panama — 6th International 35 Awards, EPHOTO Moscow' },
    { year: '2021', title: 'Honorable Mention "El Hombre En La Ciudad" — Club Fotográfico, Mendoza, Argentina' },
  ];

  const publications = [
    '"La salvación detrás del lente fotográfico" — La Estrella de Panamá, 2023',
    '"Ampolleta Roja" — Edición 10, enero 2023, pp. 68–79',
    '"Memoria, Expo 46" — Fotolibro, 2022',
    '"Arte y cultura" — Revista Prisma, Edición 7, octubre 2020, pp. 24–26',
    '"Retratos LATAM" — Edición 2, septiembre 2020, pp. 30–35',
  ];

  const education = [
    { title: 'Imago Plus — Narrativas Verbo É Imagen', hours: '8 hours' },
    { title: 'Imago Plus — Nuevas Narrativas', hours: '16 hours' },
    { title: 'Imago Plus — Formación Fotográfica y Autoral', hours: '159 hours (12 months)' },
    { title: 'Programa Sandbox — Integración Técnica en Espacio Escénico', hours: '' },
    { title: 'UDELAS — Realización de Celumetrajes & Cine de Bajo Presupuesto', hours: '80 hours' },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO
        title="Sobre mí — Edgardo Andrade | Fotógrafo Documental"
        description="Fotógrafo documental panameño. Desde 2017 documento la vida cotidiana de Panamá. Ganador de premios internacionales incluyendo World Photographic Cup 2023."
        image="/edgardo-portrait.jpg"
        type="profile"
        keywords="Edgardo Andrade, fotógrafo panameño, biografía fotógrafo, La Ruta del Metro, World Photographic Cup, exposiciones fotografía"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            PersonSchema,
            BreadcrumbSchema([
              { name: "Inicio", url: "https://www.emabraw.com" },
              { name: "Sobre mí", url: "https://www.emabraw.com/sobre-mi" }
            ])
          ]
        }}
      />

      <Navigation />
      <WhatsAppFloat />

      {/* Header Section with Two Columns */}
      <section className="px-6 sm:px-12 lg:px-24 pt-32 pb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Left Column: Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5"
            >
              <div className="sticky top-32">
                <div className="relative overflow-hidden bg-neutral-900" style={{ aspectRatio: '3/4' }}>
                  <img
                    src="/edgardo-portrait.jpg"
                    alt="Edgardo Andrade"
                    className="w-full h-full object-cover grayscale"
                  />
                </div>
              </div>
            </motion.div>

            {/* Right Column: Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-7"
            >
              <div className="space-y-12">
                {/* Bio */}
                <div>
                  <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                    Edgardo Andrade
                  </h1>
                  
                  <div className="space-y-4 text-neutral-300 text-sm leading-relaxed">
                    <p>
                      Fotógrafo documental panameño. Desde 2017, documento la vida cotidiana de mi país, su espontaneidad y ritmo. Empecé como fotógrafo aficionado buscando plasmar mis ideas a través de una cámara, y encontré en las calles de Panamá los escenarios que me motivan a contar historias.
                    </p>
                    <p>
                      Desde 2018, soy miembro de La Ruta Del Metro, proyecto con el que he crecido fotográficamente y que me ayudó a descubrir mi lenguaje visual. Mi trabajo ha sido exhibido en galerías, festivales y publicaciones tanto en Panamá como internacionalmente.
                    </p>
                    <p className="text-white font-medium">
                      En 2023, tuve el honor de representar a Panamá en la Copa Mundial de Fotografía en Singapur.
                    </p>
                  </div>
                </div>

                {/* Exhibitions */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-6">Exhibitions</h2>
                  <div className="space-y-4">
                    {exhibitions.map((item, index) => (
                      <div key={index} className="text-sm">
                        <div className="flex gap-4">
                          <span className="text-neutral-500 font-medium min-w-[40px]">{item.year}</span>
                          <div className="flex-1">
                            <p className="text-neutral-300">{item.title}</p>
                            {item.location && <p className="text-neutral-500 text-xs mt-1">{item.location}</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Awards & Honors */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-6">Awards & Honors</h2>
                  <div className="space-y-4">
                    {awards.map((item, index) => (
                      <div key={index} className="text-sm">
                        <div className="flex gap-4">
                          <span className="text-neutral-500 font-medium min-w-[40px]">{item.year}</span>
                          <p className="text-neutral-300 flex-1">{item.title}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Publications */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-6">Publications</h2>
                  <div className="space-y-3">
                    {publications.map((item, index) => (
                      <p key={index} className="text-sm text-neutral-300">{item}</p>
                    ))}
                  </div>
                </div>

                {/* Education */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-6">Education</h2>
                  <div className="space-y-4">
                    {education.map((item, index) => (
                      <div key={index} className="text-sm">
                        <p className="text-neutral-300">{item.title}</p>
                        {item.hours && <p className="text-neutral-500 text-xs mt-1">{item.hours}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Affiliations */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-6">Affiliations</h2>
                  <p className="text-sm text-neutral-300">La Ruta Del Metro — Member since 2018</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 border-t border-neutral-800/30">
        <div className="max-w-2xl mx-auto text-center">
          <a
            href="/contacto"
            className="inline-block border border-white/20 px-8 py-3 text-sm uppercase tracking-wider text-white hover:bg-white hover:text-black transition-all duration-300"
          >
            Contactar
          </a>
        </div>
      </section>
    </div>
  );
}

export default SobreMiPage;
